import { Component } from 'react'
import { HiKey } from 'react-icons/hi'
import { toast } from 'react-toastify'
import { appRef } from '../../app'
import { createLicense, deleteLicense, listLicenses, revokeLicense, updateLicense, AdminLicense } from '../../api/admin'
import { getString } from '../../locale'

type State = {
    loadingLicenses: boolean
    licenses: AdminLicense[]
    createForm: {
        licenseKey: string
        plan: string
        durationDays: number
        required: boolean
        notes: string
    }
}

export class LicenseKeyManagerPage extends Component<{}, State> {
    state: State = {
        loadingLicenses: false,
        licenses: [],
        createForm: {
            licenseKey: '',
            plan: 'monthly',
            durationDays: 30,
            required: true,
            notes: '',
        },
    }

    componentDidMount = async () => {
        appRef.setHeader(<HiKey className="me-2" size={28} />, () => getString('admin.license_manager'))
        await this.loadLicenses()
    }

    loadLicenses = async () => {
        this.setState({ loadingLicenses: true })
        try {
            const response = await listLicenses()
            this.setState({ licenses: response.data })
        } catch (error: any) {
            toast.error(error?.message || getString('admin.license_load_error'))
        } finally {
            this.setState({ loadingLicenses: false })
        }
    }

    createLicense = async () => {
        const form = this.state.createForm
        try {
            await createLicense({
                licenseKey: form.licenseKey.trim() || undefined,
                plan: form.plan.trim() || 'monthly',
                durationDays: form.durationDays,
                required: form.required ? 1 : 0,
                notes: form.notes.trim() || undefined,
            })
            toast.success(getString('admin.license_created'))
            this.setState({
                createForm: {
                    ...this.state.createForm,
                    licenseKey: '',
                    notes: '',
                },
            })
            await this.loadLicenses()
        } catch (error: any) {
            toast.error(error?.message || getString('admin.license_create_error'))
        }
    }

    updateLicenseRow = async (license: AdminLicense) => {
        const expiresAtEpoch = license.expiresAt ? Number.parseInt(license.expiresAt, 10) : null
        try {
            await updateLicense({
                id: license.id,
                licenseKey: license.licenseKey,
                plan: license.plan,
                durationDays: license.durationDays,
                required: license.required,
                notes: license.notes || '',
                assignedDeviceId: license.assignedDeviceId || '',
                revoked: license.revoked,
                expiresAt: Number.isFinite(expiresAtEpoch as number) ? expiresAtEpoch : null,
            })
            toast.success(getString('admin.license_updated'))
            await this.loadLicenses()
        } catch (error: any) {
            toast.error(error?.message || getString('admin.license_update_error'))
        }
    }

    toggleRevoke = async (license: AdminLicense) => {
        try {
            await revokeLicense(license.id, license.revoked === 1 ? 0 : 1)
            toast.success(getString('admin.license_updated'))
            await this.loadLicenses()
        } catch (error: any) {
            toast.error(error?.message || getString('admin.license_update_error'))
        }
    }

    deleteLicense = async (license: AdminLicense) => {
        if (!window.confirm(getString('admin.license_delete_confirm'))) return
        try {
            await deleteLicense(license.id)
            toast.success(getString('admin.license_deleted'))
            await this.loadLicenses()
        } catch (error: any) {
            toast.error(error?.message || getString('admin.license_delete_error'))
        }
    }

    formatEpoch(value?: string | null): string {
        if (!value) return '-'
        const epoch = Number.parseInt(value, 10)
        if (!Number.isFinite(epoch) || epoch <= 0) return '-'
        return new Date(epoch * 1000).toLocaleString()
    }

    toDateTimeInput(value?: string | null): string {
        if (!value) return ''
        const epoch = Number.parseInt(value, 10)
        if (!Number.isFinite(epoch) || epoch <= 0) return ''
        const date = new Date(epoch * 1000)
        const tzOffsetMs = date.getTimezoneOffset() * 60000
        return new Date(date.getTime() - tzOffsetMs).toISOString().slice(0, 16)
    }

    fromDateTimeInput(value: string): string | null {
        if (!value) return null
        const millis = new Date(value).getTime()
        if (!Number.isFinite(millis) || millis <= 0) return null
        return `${Math.floor(millis / 1000)}`
    }

    render() {
        const { createForm, licenses, loadingLicenses } = this.state
        return (
            <div className="space-y-6">
                <div className="rounded-xl bg-white dark:bg-gray-800 shadow p-6">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-xl font-semibold dark:text-white">{getString('admin.license_manager')}</h2>
                        <button onClick={this.loadLicenses} className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700">
                            {getString('license.refresh')}
                        </button>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">{getString('admin.license_manager_hint')}</p>

                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-2">
                        <label className="block">
                            <span className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">{getString('admin.license_key_label')}</span>
                            <input
                                type="text"
                                value={createForm.licenseKey}
                                onChange={event => this.setState({ createForm: { ...createForm, licenseKey: event.target.value } })}
                                placeholder={getString('admin.license_key_optional')}
                                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                            />
                        </label>
                        <label className="block">
                            <span className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">{getString('admin.license_plan_label')}</span>
                            <select
                                value={createForm.plan}
                                onChange={event => {
                                    const plan = event.target.value
                                    let durationDays = createForm.durationDays
                                    if (plan === 'monthly') durationDays = 30
                                    if (plan === 'yearly') durationDays = 365
                                    this.setState({ createForm: { ...createForm, plan, durationDays } })
                                }}
                                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                            >
                                <option value="monthly">{getString('admin.license_plan_monthly')}</option>
                                <option value="yearly">{getString('admin.license_plan_yearly')}</option>
                                <option value="custom">{getString('admin.license_plan_custom')}</option>
                            </select>
                        </label>
                        <label className="block">
                            <span className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">{getString('admin.license_duration_label')}</span>
                            <input
                                type="number"
                                min={1}
                                max={3650}
                                value={createForm.durationDays}
                                onChange={event => this.setState({ createForm: { ...createForm, durationDays: Number.parseInt(event.target.value, 10) || 30 } })}
                                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                            />
                        </label>
                        <label className="block">
                            <span className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">{getString('admin.license_notes_label')}</span>
                            <input
                                type="text"
                                value={createForm.notes}
                                onChange={event => this.setState({ createForm: { ...createForm, notes: event.target.value } })}
                                placeholder={getString('admin.license_notes')}
                                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                            />
                        </label>
                        <div className="flex items-end">
                            <button onClick={this.createLicense} className="w-full rounded bg-green-600 text-white hover:bg-green-700 px-3 py-2">
                                {getString('admin.license_create')}
                            </button>
                        </div>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">{getString('admin.license_duration_help')}</p>
                    <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300 mb-4">
                        <input
                            type="checkbox"
                            checked={createForm.required}
                            onChange={event => this.setState({ createForm: { ...createForm, required: event.target.checked } })}
                        />
                        {getString('license.license_required')}
                    </label>

                    {loadingLicenses && <div className="text-gray-600 dark:text-gray-300">{getString('common.loading')}</div>}
                    {!loadingLicenses && licenses.length === 0 && (
                        <div className="text-gray-600 dark:text-gray-300">{getString('admin.license_no_records')}</div>
                    )}
                    <div className="space-y-3">
                        {licenses.map(license => (
                            <div key={license.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
                                    <input
                                        type="text"
                                        value={license.licenseKey}
                                        onChange={event =>
                                            this.setState({
                                                licenses: licenses.map(row =>
                                                    row.id === license.id ? { ...row, licenseKey: event.target.value.toUpperCase() } : row,
                                                ),
                                            })
                                        }
                                        className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                                    />
                                    <input
                                        type="text"
                                        value={license.plan}
                                        onChange={event =>
                                            this.setState({
                                                licenses: licenses.map(row => (row.id === license.id ? { ...row, plan: event.target.value } : row)),
                                            })
                                        }
                                        className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                                    />
                                    <input
                                        type="number"
                                        min={1}
                                        max={3650}
                                        value={license.durationDays}
                                        onChange={event =>
                                            this.setState({
                                                licenses: licenses.map(row =>
                                                    row.id === license.id
                                                        ? { ...row, durationDays: Number.parseInt(event.target.value, 10) || row.durationDays }
                                                        : row,
                                                ),
                                            })
                                        }
                                        className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                                    />
                                    <input
                                        type="text"
                                        value={license.assignedDeviceId || ''}
                                        onChange={event =>
                                            this.setState({
                                                licenses: licenses.map(row =>
                                                    row.id === license.id ? { ...row, assignedDeviceId: event.target.value.toUpperCase() } : row,
                                                ),
                                            })
                                        }
                                        placeholder={getString('admin.license_device_id')}
                                        className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                                    />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                                    <input
                                        type="text"
                                        value={license.notes || ''}
                                        onChange={event =>
                                            this.setState({
                                                licenses: licenses.map(row => (row.id === license.id ? { ...row, notes: event.target.value } : row)),
                                            })
                                        }
                                        placeholder={getString('admin.license_notes')}
                                        className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                                    />
                                    <label className="block">
                                        <span className="block text-xs text-gray-600 dark:text-gray-300 mb-1">{getString('license.expiry')}</span>
                                        <input
                                            type="datetime-local"
                                            value={this.toDateTimeInput(license.expiresAt)}
                                            onChange={event =>
                                                this.setState({
                                                    licenses: licenses.map(row =>
                                                        row.id === license.id
                                                            ? { ...row, expiresAt: this.fromDateTimeInput(event.target.value) }
                                                            : row,
                                                    ),
                                                })
                                            }
                                            className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                                        />
                                    </label>
                                    <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300 px-1">
                                        <input
                                            type="checkbox"
                                            checked={license.required === 1}
                                            onChange={event =>
                                                this.setState({
                                                    licenses: licenses.map(row =>
                                                        row.id === license.id ? { ...row, required: event.target.checked ? 1 : 0 } : row,
                                                    ),
                                                })
                                            }
                                        />
                                        {getString('license.license_required')}
                                    </label>
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                                    <div>{getString('license.status')}: {license.revoked === 1 ? getString('license.status_revoked') : getString('license.status_active')}</div>
                                    <div>{getString('license.activation')}: {this.formatEpoch(license.activatedAt)}</div>
                                    <div>{getString('license.expiry')}: {this.formatEpoch(license.expiresAt)}</div>
                                    <div>{getString('license.temp_unlock_until')}: {this.formatEpoch(license.tempUnlockUntil)}</div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button onClick={() => this.updateLicenseRow(license)} className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700">
                                        {getString('common.save')}
                                    </button>
                                    <button
                                        onClick={() => this.toggleRevoke(license)}
                                        className={`px-3 py-2 rounded text-white transition-colors ${license.revoked === 1 ? 'hover:bg-emerald-700' : 'hover:bg-orange-700'}`}
                                        style={{ backgroundColor: license.revoked === 1 ? '#059669' : '#ea580c' }}
                                    >
                                        {license.revoked === 1 ? getString('admin.license_unrevoke') : getString('admin.license_revoke')}
                                    </button>
                                    <button onClick={() => this.deleteLicense(license)} className="px-3 py-2 rounded bg-red-600 text-white hover:bg-red-700">
                                        {getString('common.delete')}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )
    }
}
